// Auto-settlement: turn a captured closing price and a final result into a
// leg's CLV and a bet's outcome, without anybody having to type it in.
//
// The pure half is at the top — five small functions with no I/O in them,
// which is also the whole of what settlement.test.js exercises: what a leg's
// outcome is, what a whole multi's is, what it returns, which closing row to
// credit a leg with, and a bet's own CLV. Everything below that is the driver
// that reads storage, asks the server for closing days, and writes the
// results back — it is built ON those functions rather than reimplementing
// their opinions inline, but it is not itself unit-tested: the wiring is
// exercised by hand (see the ticket's `curl` check) the way store.js's own
// IndexedDB calls are.
//
// A leg only ever gets its CLOSING PRICE and CLV from here — never a
// bookmaker's settlement, which the punter still has to check themselves.
// `resultSource: 'auto'` says so on every stub HistoryStub prints one of.

import { ref } from 'vue';
import { apiBase } from '../api-base.js';
import { records, prefs } from '../../core/storage.js';
import { isDemo } from '../../core/demo.js';
import { storeReady, betRecords, recordResult, addDay } from '../../store.js';
import { localDate } from '../tickets.js';
import { proposalsFor, rejectionsFor } from '../proposals.js';
import { clvFair } from '../engine/metrics.js';
import { consensus } from '../engine/universe.js';
import { bookFairNet } from '../engine/models/sharpAnchor.js';
import { RACING_OVERROUND_BAND } from '../racing/board.js';
import { SPORTS } from '../engine/teams.js';
import { getOddsApiKey } from '../odds.js';
import { keyHeaders } from '../providers.js';
import { legEventStart } from '../results-due.js';

const SYDNEY = 'Australia/Sydney';
const SYNC_MIN_MS = 5 * 60 * 1000;
const CLOSING_CACHE_KEY = 'pref.closingDays';

/** Where the paper trail lives: `{ 'YYYY-MM-DD': [settled proposals] }`. */
const PROPOSAL_OUTCOMES_KEY = 'proposalOutcomes';

/**
 * And the settled half of the refusal log, in the same shape and its own key.
 *
 * Kept apart from the proposals for the reason `proposals.js` gives at length:
 * every consumer of the trail counts a row as something the app said it would
 * bet, and a refusal is the opposite claim. It settles through exactly the same
 * `settleProposal`, so a refused leg gets a result and a closing price like any
 * other — it costs nothing to score, because the close it needs has already
 * been bought for the day it is on.
 */
export const REJECTION_OUTCOMES_KEY = 'rejectionOutcomes';

/** How much of the trail is worth keeping on a phone. Evidence looks back 30. */
const OUTCOME_KEEP_DAYS = 60;

const cents = (n) => Math.round(n * 100) / 100;

// --- which row belongs to which leg ---------------------------------------------
//
// A LEG FINDS ITS ROW BY THE PROVIDER'S EVENT ID FIRST, AND BY `matchKey` ONLY
// AFTER THAT.
//
// The key was the only answer until 2026-09-16, and it is a DERIVED one: sport,
// the fixture's local DATE, the two team ids. Everything in it can be computed
// twice and come out twice the same — except the date, which for a sport the
// registry does not carry (`SPORTS` has no MLB) used to be dated in whichever
// zone the machine was in. The phone built `baseball_mlb:2026-09-15:…` off a
// 22:41Z first pitch; the server, in UTC, wrote the result row as
// `baseball_mlb:2026-09-14:…`; and the leg read NO RESULT beside a score that
// was sitting in the file. `matchKey` is zone-fixed now (`APP_TZ`, teams.js), so
// new rows and new legs agree — but the rows already stored carry the old key
// for ever, and rewriting somebody's history is not a repair.
//
// The provider's event id is not derived. The Odds API hands the same `id` to
// the board the ticket was printed off and to the scores call that settles it,
// both engines already carry it onto the leg (`eventId`/`providerEventId`) and
// `fetchScores` already writes it onto every result row. So it is asked first.
//
// FALLING BACK RATHER THAN CHOOSING is deliberate: racing results carry no
// provider id at all (`lib/racing/results.js` keys on `matchKey` and a
// zone-fixed `raceKey`), and every leg written before the engine has an id of
// nothing. Anything that matched by key yesterday still matches by key today.

/** The event id this leg was printed with, or null. */
const legEventId = (leg) => leg?.providerEventId ?? leg?.eventId ?? null;

/**
 * The rows of `list` that describe this leg's fixture: the ones carrying its
 * provider event id, else the ones carrying its matchKey. Empty when neither
 * side has anything to join on — two rows with no key are not one match.
 */
function rowsFor(list, leg) {
  const rows = list ?? [];
  const id = legEventId(leg);
  if (id != null) {
    const byId = rows.filter((r) => r?.providerEventId != null && r.providerEventId === id);
    if (byId.length) return byId;
  }
  const key = leg?.matchKey;
  if (key == null) return [];
  return rows.filter((r) => r?.matchKey != null && r.matchKey === key);
}

// --- the pure core -------------------------------------------------------------

/** The day's row for this leg's match, or null. */
const rowFor = (leg, results) => rowsFor(results, leg)[0] ?? null;

/**
 * What a leg's result is, from the day's results (an array of ResultRow —
 * `{ matchKey, winner, ... }`). `null` while the match is still unresolved:
 * absent from `results` entirely, or present with `winner: null`.
 *
 * RACING ADDS THREE ANSWERS AND CHANGES NONE OF THE OLD ONES. A racing row
 * (`lib/racing/results.js`) carries a `status`, a list of scratchings and a dead
 * heat, and a football row carries none of them, so every branch below is
 * unreachable on the rows this app settled before racing landed.
 *
 *   abandoned   the meeting never ran: every leg on the race voids, whatever
 *               else the row says.
 *   interim     the placings are published and not yet final — a protest, a
 *               photo and a late scratching all move them, so nothing settles
 *               off one. The leg stays pending and says why (`awaitingFinal`).
 *   scratched   the runner never left the barrier. Void, like a draw: the
 *               bookmaker gives the stake back.
 *   dead heat   a win, at a share of the price. WHICH runner `winner` names is
 *               arbitrary in a dead heat, so the dead-heat list is asked before
 *               it — otherwise the second name on a two-horse photo would be
 *               settled as a loser.
 */
export function legOutcome(leg, results) {
  const row = rowFor(leg, results);
  if (!row) return null;
  if (row.status === 'abandoned') return 'void';
  if (row.status === 'interim') return null;
  if (row.scratched?.includes(leg?.side)) return 'void';
  if (row.winner == null) return null;
  if (row.winner === 'draw') return 'void';
  if (row.deadHeat?.includes(leg?.side)) return 'won';
  return row.winner === leg.side ? 'won' : 'lost';
}

// --- what a won racing leg is actually paid at ---------------------------------
//
// A football leg pays its price. A racing leg pays its price times a FACTOR, and
// the factor is the one thing in settlement that cannot be re-derived later: a
// deduction is the settling bookmaker's own figure for a scratching that had
// already happened when the bet was struck, and next week's results feed will
// have forgotten it. So it is computed once, written onto the leg, and read back
// on every pass afterwards — `resolveLeg` treats a stored factor as final, the
// same way it treats a hand mark as final.

/** The six fields a racing leg carries about HOW it settled. */
const MARK_KEYS = ['factor', 'deadHeat', 'deduction', 'deductionRunners', 'voidReason', 'awaitingFinal'];

/** Is this leg a race? The sport id, or the matchKey it was built from. */
const isRacingLeg = (leg) =>
  String(leg?.sport ?? String(leg?.matchKey ?? '').split(':')[0] ?? '').startsWith('racing_');

/** Six decimal places: a factor is a payout multiplier, not a float to compound. */
const round6 = (n) => Math.round(n * 1e6) / 1e6;

const sameMark = (a, b) => (Array.isArray(a) && Array.isArray(b)
  ? a.length === b.length && a.every((v, i) => v === b[i])
  : a === b);

/**
 * What the scratchings on this race take out of a winning fixed-odds bet, as a
 * fraction of the dollar, and which runners caused it.
 *
 * SUMMED, and clamped below a dollar. Australian books publish one deduction per
 * scratching and apply them together; two scratchings at 12% and 8% is 20 cents
 * in the dollar off, not 0.88 × 0.92. The clamp is arithmetic hygiene, not a
 * rule — a race deducting the whole dollar is a race nobody could have a winning
 * bet on — and it stops a bad feed turning a return into a negative number.
 */
function deductionOn(row) {
  const win = row?.deductions?.win ?? {};
  const runners = [];
  let total = 0;
  for (const [selection, value] of Object.entries(win)) {
    const v = Number(value);
    if (!Number.isFinite(v) || v <= 0) continue;
    total += v;
    runners.push(selection);
  }
  if (!runners.length) return null;
  return { total: Math.min(round6(total), 0.99), runners };
}

/**
 * The racing marks for a leg, as fields to write: `{}` for a leg that is not a
 * race, so a football leg comes out of `resolveLeg` byte for byte what it was.
 *
 * `outcome` is `legOutcome`'s answer for this same pass, passed in rather than
 * recomputed — there is one opinion about what a leg did and this is not a
 * second one.
 */
export function racingMarks(leg, results, outcome) {
  if (!isRacingLeg(leg)) return {};
  const row = rowFor(leg, results);

  const marks = {
    factor: leg?.factor ?? null,
    deadHeat: leg?.deadHeat ?? null,
    deduction: leg?.deduction ?? null,
    deductionRunners: leg?.deductionRunners ?? null,
    voidReason: leg?.voidReason ?? null,
    // The one mark that is a fact about RIGHT NOW rather than about the bet, so
    // it is the one mark that is recomputed every pass and cleared when the race
    // is made final.
    awaitingFinal: row?.status === 'interim' && outcome == null ? true : null,
  };

  // WRITTEN ONCE. A stored factor is the bookmaker's arithmetic as it stood on
  // the day, and a later results pull that has dropped the deduction must not be
  // able to quietly pay the punter more than they were paid.
  if (leg?.factor != null || !row) return marks;

  if (outcome === 'void') {
    marks.voidReason = row.status === 'abandoned' ? 'abandoned'
      : row.scratched?.includes(leg?.side) ? 'scratched'
        : marks.voidReason;
    return marks;
  }
  if (outcome !== 'won') return marks;

  const heat = row.deadHeat?.includes(leg?.side) ? row.deadHeat.length : null;
  const deduction = deductionOn(row);
  let factor = 1;
  if (heat) factor /= heat;
  if (deduction) factor *= 1 - deduction.total;
  if (factor === 1) return marks;

  marks.factor = round6(factor);
  if (heat) marks.deadHeat = heat;
  if (deduction) {
    marks.deduction = deduction.total;
    marks.deductionRunners = deduction.runners;
  }
  return marks;
}

/**
 * A whole multi's outcome from its legs' own `result`s: one lost leg loses
 * the multi outright, whatever the others are doing or still waiting on;
 * otherwise `null` until every leg has an answer, then void only if every
 * leg was void, and won for anything else.
 */
export function betOutcome(legs) {
  if (!legs?.length) return null;
  if (legs.some((l) => l.result === 'lost')) return 'lost';
  if (legs.some((l) => l.result == null)) return null;
  if (legs.every((l) => l.result === 'void')) return 'void';
  return 'won';
}

/**
 * What a stake returns for these legs, in dollars and cents: lost is nothing,
 * otherwise the stake times every non-void leg's own price — a void leg
 * contributes nothing to the product (as if it had never been in the multi),
 * which is also why an all-void multi correctly returns the stake itself.
 *
 * TIMES THE LEG'S OWN FACTOR, where it has one. A racing leg that dead-heated or
 * carried a scratching deduction was paid at a share of its price (see
 * `racingMarks`), and the share is on the leg because the bookmaker's arithmetic
 * is not re-derivable a week later. No football leg has ever had one, so `?? 1`
 * is the whole of what changed for them.
 */
export function returnedFor(stake, legs) {
  if (betOutcome(legs) === 'lost') return 0;
  const factor = (legs ?? []).reduce(
    (acc, l) => (l.result === 'void' ? acc : acc * l.price * (l.factor ?? 1)),
    1,
  );
  return cents((Number(stake) || 0) * factor);
}

/**
 * The closing row to credit a leg with: the same bookmaker it was taken at,
 * falling back to the exchange, falling back to whichever book priced it —
 * a closing line from ANY book beats none at all. `null` if nobody priced
 * this match's selection by the time it was captured.
 */
export function pickClosing(rows, leg) {
  const candidates = rowsFor(rows, leg).filter((r) => r?.selection === leg?.side);
  if (!candidates.length) return null;
  const at = (bookmaker) => candidates.find((r) => r.bookmaker === bookmaker);
  const row = (leg.bookmakerKey && at(leg.bookmakerKey)) || at('betfair_ex_au') || candidates[0];
  return { price: row.price, bookmaker: row.bookmaker };
}

/**
 * A whole bet's CLV: every leg's own CLV compounded, over the legs that have
 * one. `null` when none of them do — a multi with no closing data anywhere is
 * not "0% CLV", it is unmeasured.
 *
 * READ OFF THE LEGS, not recomputed from their prices. A leg's CLV is a
 * question about the devigged close and which book's it was (`closingFairFor`),
 * and a second opinion here would be the own-book arithmetic engine review #6
 * exists to remove. A leg with a figure but no `clvSource` is a leg settled
 * before that rule and never re-derived: it is skipped, because the number
 * beside it was measured against something else.
 */
export function betClv(legs) {
  const priced = (legs ?? []).filter((l) => l?.clvSource && Number.isFinite(l?.clv));
  if (!priced.length) return null;
  return priced.reduce((acc, l) => acc * (1 + l.clv), 1) - 1;
}

/** Would this placed bet auto-settle right now, given its (already resolved) legs? */
export function shouldAutoSettle(bet, legs) {
  if (!bet || bet.autoSettle === false) return false;
  if (bet.result !== 'pending') return false;
  if (bet.resultSource === 'manual') return false;
  return betOutcome(legs) !== null;
}

// --- what the close is actually worth --------------------------------------------
//
// CLV USED TO BE MEASURED AGAINST THE BET'S OWN BOOK, GROSS OF VIG (engine
// review #6). `pickClosing` prefers the bookmaker the bet was struck at, and the
// old figure was `taken / thatBook'sClose - 1` — so a book that opened fat and
// reverted to the field printed CLV for nobody's skill, and margin drift counted
// as beating the market. It flattered exactly the strategies built to find a
// book out of line, which are the ones the app most needs to be honest about.
//
// The reference is now a PROBABILITY, in this order:
//
//   exchange  the Betfair close, devigged, net of commission — the one price on
//             the board that moved because somebody's money moved it, and the
//             closest thing to a settled opinion the market has;
//   market    the board's own devigged consensus WITHOUT the bet's own book, so
//             the counter the punter beat does not get a vote on whether it was
//             beaten;
//   null      neither. There is no CLV here, and a leg says so rather than
//             falling back to the one number this whole note is about.
//
// `pickClosing` still chooses the closing PRICE that gets printed beside the
// leg: what the punter wants to see is the number their own book settled at.
// The two answers are allowed to disagree, and usually do.

/** The exchange whose close is the first reference. Same key `pickClosing` falls back to. */
const CLV_EXCHANGE = 'betfair_ex_au';

/**
 * A day's closing rows for one match, in the shape the engine reads.
 *
 * A RACE IS NOT HOME AND AWAY. `selectionsOf` defaults to the h2h three, so a
 * match built without `selections` devigs a fourteen-runner field as if it were
 * a football fixture and answers null for every runner. The selections are the
 * ones the closing rows actually carry, and a race brings its own overround
 * band with it — a twelve-runner card is routinely 120% and the h2h band would
 * throw the whole thing away.
 */
function closingMatch(closingRows, leg) {
  const relevant = rowsFor(closingRows, leg);
  if (!relevant.length) return null;
  const prices = {};
  const selections = [];
  for (const row of relevant) {
    if (!prices[row.selection]) selections.push(row.selection);
    (prices[row.selection] ??= []).push({ bookmaker: row.bookmaker, price: row.price, liquidity: row.liquidity ?? null });
  }
  return isRacingLeg(leg)
    ? { prices, selections, overroundBand: [...RACING_OVERROUND_BAND] }
    : { prices, selections };
}

/**
 * The closing fair probability a leg's CLV is measured against, and where it
 * came from: `{ fair, source }` with `source` 'exchange', 'market' or null.
 *
 * A leg with no `bookmakerKey` — every leg written before the strategy engine —
 * excludes nothing from the market fallback. That is not the own-book close
 * coming back in: it is a weighted vote of every book that priced the match,
 * and the one thing it cannot be is one book's margin measured against itself.
 */
export function closingFairFor(closingRows, leg) {
  const match = closingMatch(closingRows, leg);
  const side = leg?.side;
  if (!match || !side) return { fair: null, source: null };

  if ((match.prices[side] ?? []).some((r) => r.bookmaker === CLV_EXCHANGE)) {
    const p = bookFairNet(match, side, CLV_EXCHANGE);
    // WHOSE CLOSE IT IS, and on a race that is a real distinction. A BSP is the
    // one price everybody could have had at the one instant everybody had it,
    // and it is what the free ingest brings; an exchange row off a price
    // history is one book's last quote before the jump. Both devig the same
    // way, and the trail says which it measured against.
    const bsp = rowsFor(closingRows, leg).some((r) => r?.selection === side && r?.kind === 'bsp');
    if (Number.isFinite(p) && p > 0 && p < 1) return { fair: p, source: bsp ? 'bsp' : 'exchange' };
  }

  const p = consensus(match, side, { exclude: leg?.bookmakerKey ?? null })?.pFair;
  if (Number.isFinite(p) && p > 0 && p < 1) return { fair: p, source: 'market' };

  return { fair: null, source: null };
}

/**
 * One leg, brought up to date from a day's closing rows and results.
 * `resultSource: 'manual'` freezes a leg entirely — nothing here ever
 * overwrites a manual call. Returns the SAME object when nothing changed, so
 * a caller can tell what actually needs writing back with `!==`.
 *
 * THE BACKFILL LIVES HERE, and it is one pass: a leg that arrives with a CLV
 * and no `clvSource` was settled the old way, so the old figure is kept under
 * `clvLegacy` (nothing prints it; it is there so the two can be compared, and
 * so a record's own history is not quietly rewritten) and the new one is
 * derived from this day's rows. A day whose closing rows have aged out is not
 * re-derived at all — there is nothing to derive from — so the leg keeps what
 * it has, `clvSource` and all.
 */
export function resolveLeg(leg, results, closingRows, nowIso = new Date().toISOString()) {
  if (leg.resultSource === 'manual') return leg;

  const picked = pickClosing(closingRows, leg);
  const closingPrice = picked ? picked.price : (leg.closingPrice ?? null);
  const derived = picked ? closingFairFor(closingRows, leg) : null;
  const closingFair = derived ? derived.fair : (leg.closingFair ?? null);
  const clvSource = derived ? derived.source : (leg.clvSource ?? null);
  const legClv = derived
    ? clvFair({ priceTaken: leg.price, pClosingFair: derived.fair })
    : (leg.clv ?? null);
  const clvLegacy = derived && leg.clvSource == null && Number.isFinite(leg.clv)
    ? leg.clv
    : (leg.clvLegacy ?? null);
  const outcome = legOutcome(leg, results);
  const nextResult = outcome ?? leg.result ?? null;
  // `{}` for every leg that is not a race, which is what keeps the sports path
  // byte-identical: nothing is spread, and nothing is compared.
  const marks = racingMarks(leg, results, outcome);

  const changed = closingPrice !== (leg.closingPrice ?? null)
    || closingFair !== (leg.closingFair ?? null)
    || clvSource !== (leg.clvSource ?? null)
    || legClv !== (leg.clv ?? null)
    || clvLegacy !== (leg.clvLegacy ?? null)
    || nextResult !== (leg.result ?? null)
    || MARK_KEYS.some((k) => k in marks && !sameMark(marks[k], leg[k] ?? null));
  if (!changed) return leg;

  return {
    ...leg,
    ...marks,
    closingPrice,
    closingFair,
    clvSource,
    clvLegacy,
    clv: legClv,
    result: nextResult,
    resultSource: outcome != null ? 'auto' : (leg.resultSource ?? null),
    settledAt: outcome != null ? nowIso : (leg.settledAt ?? null),
  };
}

// --- the driver ------------------------------------------------------------------

export const settling = ref(false);
export const lastSync = ref(null);

/** The signed-in punter's token as a header. Imported late so a build without auth still runs. */
async function authHeaders() {
  try {
    const mod = await import('../../../addons/auth/index.js');
    const token = await mod.getIdToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function fetchClosingDay(date) {
  const res = await fetch(`${apiBase()}/api/closing?date=${encodeURIComponent(date)}`, {
    headers: { Accept: 'application/json', ...(await authHeaders()) },
  });
  if (!res.ok) throw new Error(`closing ${date}: HTTP ${res.status}`);
  const body = await res.json();
  return {
    fetchedAt: new Date().toISOString(),
    closing: Array.isArray(body.closing) ? body.closing : [],
    results: Array.isArray(body.results) ? body.results : [],
  };
}

/**
 * The closing days that matter right now, cached in `prefs`. A date with
 * nothing left unresolved keeps its cached answer indefinitely — there is
 * nothing more to learn about it — everything else is asked again after five
 * minutes, or immediately when `force` is set.
 */
async function loadClosingDays(dateInfo, { force = false } = {}) {
  const cache = { ...(prefs[CLOSING_CACHE_KEY] || {}) };
  const out = new Map();
  await Promise.all([...dateInfo.entries()].map(async ([date, info]) => {
    const cached = cache[date];
    const freshEnough = cached && (!info.anyUnresolved || Date.now() - Date.parse(cached.fetchedAt || 0) < SYNC_MIN_MS);
    if (cached && freshEnough && !force) {
      out.set(date, cached);
      return;
    }
    try {
      const entry = await fetchClosingDay(date);
      cache[date] = entry;
      out.set(date, entry);
    } catch {
      if (cached) out.set(date, cached);
    }
  }));
  prefs[CLOSING_CACHE_KEY] = cache;
  return out;
}

// --- racing: the results, and the closing line -----------------------------------
//
// EVERY RULE HERE IS A CREDIT RULE. The punter's PuntersEdge key is 1,500 credits
// a month and a day's card already spends about forty of them, so settlement gets
// a fixed, small allowance and never a poll:
//
//   /api/racing/results        2 credits, at most TWICE a day, and only while a
//                              racing leg is pending on a race that jumped at
//                              least fifteen minutes ago.
//   /api/racing/bsp            FREE, and therefore first and for every race.
//                              The daily ingest already brings the Betfair
//                              starting price for every AU/NZ runner and the BSP
//                              IS the close, so a race in an ingested month has
//                              its closing line bought already. It reads a
//                              stored month on Legwork's own server and fetches
//                              nothing from anybody.
//   /api/racing/price-history  5 credits, at most FOUR a day, one per race, and
//                              only for a race a bet was actually PLACED on AND
//                              that the ingest has not reached — the Hub
//                              publishes a month at a time, so that is this
//                              week's races and none of last month's. CLV on a
//                              paper ticket is not worth five credits; on a free
//                              one it is worth having.
//
// Both counters are stamped into `prefs` against the Sydney date, so closing and
// reopening the app cannot buy a second allowance, and both are read from the
// device's own cache first: a row already bought settles for ever, free.
//
// AND EVERYTHING IT LEARNS GOES INTO THE SAME TWO ARRAYS THE SPORTS PATH USES.
// `toResultRows` hands back rows keyed by `matchKey` and the price history is
// turned into closing rows in `pickClosing`'s own shape, so `resolveLeg` never
// learns that racing exists. That is the whole reason this is a merge and not a
// second settle path.

/** Where the bought rows and the day's allowance live. */
const RACING_CACHE_KEY = 'pref.racingSettle';

/** How long after the off a race is worth asking about. */
const RACING_JUMP_GRACE_MS = 15 * 60 * 1000;

/** The day's allowance: two results pulls (2 cr each), four races priced (5 cr each). */
const RACING_RESULT_PULLS = 2;
const RACING_CLOSING_PULLS = 4;

/** A status that will never change again — nothing is worth re-buying. */
const RACING_DECIDED = new Set(['final', 'abandoned']);

/** The device's racing settle store, in a shape nothing downstream has to guard. */
function readRacingStore() {
  let raw = null;
  try { raw = prefs[RACING_CACHE_KEY]; } catch { raw = null; }
  const days = raw?.days && typeof raw.days === 'object' ? raw.days : {};
  const pulls = raw?.pulls && typeof raw.pulls === 'object' ? raw.pulls : {};
  return {
    days: { ...days },
    pulls: {
      date: typeof pulls.date === 'string' ? pulls.date : null,
      results: Number(pulls.results) || 0,
      closing: Number(pulls.closing) || 0,
    },
  };
}

/** One day's bought rows, in the shape the merge reads. */
const racingDay = (store, date) => ({
  results: Array.isArray(store.days[date]?.results) ? store.days[date].results : [],
  closing: Array.isArray(store.days[date]?.closing) ? store.days[date].closing : [],
  priced: Array.isArray(store.days[date]?.priced) ? store.days[date].priced : [],
});

/**
 * The unresolved racing legs worth spending on, by meeting date.
 *
 * `placed` is the subset a bet was actually struck on, and it is the only subset
 * the five-credit closing call will ever look at. Both halves read
 * `legEventStart`, so a race that jumped four minutes late is fifteen minutes
 * old from the moment it actually ran rather than from the moment the card said
 * it would.
 */
function racingLegsDue(waiting, proposalsByDate, now) {
  const byDate = new Map();
  const add = (leg, placed) => {
    const start = Date.parse(legEventStart(leg) ?? '');
    if (!Number.isFinite(start) || start + RACING_JUMP_GRACE_MS > now) return;
    const date = localDate(start, SYDNEY);
    const entry = byDate.get(date) ?? { keys: new Set(), placed: new Map() };
    entry.keys.add(leg.matchKey);
    if (placed && !entry.placed.has(leg.matchKey)) entry.placed.set(leg.matchKey, leg);
    byDate.set(date, entry);
  };

  for (const { leg } of waiting) {
    if (isRacingLeg(leg)) add(leg, true);
  }
  for (const list of proposalsByDate.values()) {
    for (const proposal of list ?? []) {
      for (const leg of proposal?.legs ?? []) {
        if (leg?.result != null || !isRacingLeg(leg)) continue;
        add(leg, false);
      }
    }
  }
  return byDate;
}

/**
 * The closing rows a race's price history is worth, in `pickClosing`'s shape.
 *
 * DROPS EVERY SERIES THAT DID NOT REACH THE JUMP, and that is the point of the
 * call. The vendor measures 63% of its series ending inside 120 seconds of the
 * off and 81% inside 300; the rest are a book that stopped quoting, and a price
 * from twenty minutes out is not a closing line — crediting one would print a
 * CLV that says the punter beat a market that had not finished moving.
 *
 * The exchange's key is normalised to the one `pickClosing` falls back to, so a
 * BSP that is there is found as the BSP.
 */
/**
 * A race's starting prices as closing rows, in `pickClosing`'s own shape.
 *
 * THE BSP IS THE CLOSE, AND IT IS FREE. It is the price everybody could have
 * had at the one instant everybody had it — Betfair's own calibration puts it
 * within 0.2 of a point over 380,000 ANZ races — and the daily ingest already
 * brings it for every runner. So for any race in an ingested month the closing
 * line costs nothing, against five credits for a price history, and it is a
 * better number besides: a price history's "close" is each book's last quote,
 * which is a book that stopped quoting as often as it is a book at the jump.
 *
 * Marked `kind: 'bsp'` so `closingFairFor` can say where the figure came from.
 * A runner with no offer carries a null price and is left out: the field is
 * what the file says it was, and a runner nobody priced is not a price.
 */
export function closingRowsFromBsp(bsp, matchKey) {
  const rows = [];
  for (const runner of bsp?.runners ?? []) {
    const number = Number(runner?.number);
    const price = Number(runner?.bsp);
    if (!Number.isFinite(number) || !Number.isFinite(price) || price <= 1) continue;
    rows.push({
      matchKey,
      selection: `r${number}`,
      bookmaker: 'betfair_ex_au',
      price,
      kind: 'bsp',
    });
  }
  // A single price is not a market: `bookFairNet` cannot devig a one-runner
  // vector, and a CLV built on one would be a fact about nothing.
  return rows.length >= 2 ? rows : [];
}

export function closingRowsFromHistory(history, matchKey) {
  const rows = [];
  for (const runner of history?.runners ?? []) {
    const number = Number(runner?.number);
    if (!Number.isFinite(number)) continue;
    const selection = `r${number}`;
    for (const book of runner?.bookmakers ?? []) {
      const price = Number(book?.close_price);
      const secs = Number(book?.close_secs_to_jump);
      if (!Number.isFinite(price) || price <= 1) continue;
      if (!Number.isFinite(secs) || secs > 300) continue;
      const key = String(book?.key ?? '');
      if (!key) continue;
      rows.push({
        matchKey,
        selection,
        bookmaker: /^betfair/i.test(key) ? 'betfair_ex_au' : key,
        price,
      });
    }
  }
  return rows;
}

/**
 * Buy what settlement is missing, inside the day's allowance, and merge it into
 * the days the sports path already built.
 *
 * Merged into EVERY day rather than into the one the race falls on, deliberately:
 * a proposal is filed under the day the pad was dealt and a placed leg under the
 * day it starts, and those are not always the same date. The rows are keyed by
 * `matchKey`, so a row in a day that has no leg on it is read by nobody and
 * costs a comparison.
 *
 * Never throws: this is an extra, and a racing feed that is down must not be able
 * to cost a football bet its settlement.
 */
async function settleRacing(closingByDate, waiting, proposalsByDate, now) {
  const due = racingLegsDue(waiting, proposalsByDate, now);
  if (!due.size) return;

  const {
    fetchRacingResults, fetchRacingPriceHistory, fetchRaceBsp, cachedRacingCard,
  } = await import('../racing/fetch.js');
  const { toResultRows, raceIndex } = await import('../racing/results.js');

  const store = readRacingStore();
  const today = localDate(now, SYDNEY);
  if (store.pulls.date !== today) store.pulls = { date: today, results: 0, closing: 0 };

  let changed = false;

  for (const [date, entry] of [...due.entries()].sort()) {
    const day = racingDay(store, date);
    const byKey = new Map(day.results.map((r) => [r?.matchKey, r]));

    // Nothing to learn: every leg on this date already has a row that will never
    // change again. The commonest case by far, and it spends nothing.
    const missing = [...entry.keys].filter((k) => !RACING_DECIDED.has(byKey.get(k)?.status));
    if (missing.length && store.pulls.results < RACING_RESULT_PULLS) {
      store.pulls.results += 1;
      changed = true;
      const { results, status } = await fetchRacingResults(date);
      // A pull the server refused before it reached the upstream cost nothing,
      // so it does not cost an allowance either — and it says nothing about the
      // day, so nothing is written from it.
      if (status !== 'ready') {
        store.pulls.results -= 1;
      } else if (results.length) {
        const card = cachedRacingCard(date);
        const rows = toResultRows(results, raceIndex(card?.races ?? []));
        // Only the races this device is waiting on are kept. A day's AU card is
        // two hundred races and a phone's localStorage is not the place for it.
        for (const row of rows) {
          if (entry.keys.has(row.matchKey)) byKey.set(row.matchKey, row);
        }
        day.results = [...byKey.values()];
      }
    }

    const priced = new Set(day.priced);

    // THE FREE CLOSE FIRST, AND FOR EVERY RACE RATHER THAN FOR PLACED BETS
    // ALONE. The daily ingest brings the Betfair starting price for every AU/NZ
    // runner and the BSP is the close, so a race in an ingested month has its
    // closing line already bought. It costs nothing, so the paper trail gets
    // one too — which is the whole point of a paper trail: a proposal nobody
    // backed is still evidence about the strategy that proposed it.
    for (const matchKey of entry.keys) {
      if (priced.has(matchKey)) continue;
      const { bsp, status } = await fetchRaceBsp(date, matchKey);
      if (status !== 'ready') continue;
      const rows = bsp ? closingRowsFromBsp(bsp, matchKey) : [];
      if (!rows.length) continue;
      // Marked priced: the ingest does not change its mind about a race, so
      // there is nothing to learn by asking again, and the five-credit call
      // below is now never made for this one.
      priced.add(matchKey);
      day.priced = [...priced];
      day.closing = [...day.closing.filter((r) => r?.matchKey !== matchKey), ...rows];
      changed = true;
    }

    // The closing line the hard way, for placed bets only, and only for a race
    // the ingest has not reached. One race, one call, five credits.
    for (const [matchKey, leg] of entry.placed) {
      if (store.pulls.closing >= RACING_CLOSING_PULLS) break;
      if (priced.has(matchKey) || leg?.closingPrice != null) continue;
      const raceId = byKey.get(matchKey)?.raceId ?? null;
      if (!raceId) continue;

      store.pulls.closing += 1;
      changed = true;
      const { history, status } = await fetchRacingPriceHistory(raceId);
      if (status !== 'ready') {
        store.pulls.closing -= 1;
        continue;
      }
      // Marked priced whatever came back: a race whose series never reached the
      // jump has no closing line, and asking again tomorrow buys the same answer
      // for another five credits.
      priced.add(matchKey);
      day.priced = [...priced];
      day.closing = [...day.closing.filter((r) => r?.matchKey !== matchKey), ...closingRowsFromHistory(history, matchKey)];
    }

    store.days[date] = day;
  }

  // Older than the trail keeps, and nothing can ask for it again.
  const cutoff = addDay(today, -OUTCOME_KEEP_DAYS);
  for (const date of Object.keys(store.days)) {
    if (date >= cutoff) continue;
    delete store.days[date];
    changed = true;
  }
  if (changed) {
    try { prefs[RACING_CACHE_KEY] = store; } catch { /* private mode */ }
  }

  // The merge. Every row this device has ever bought, into every day the settle
  // is about to read.
  const results = Object.values(store.days).flatMap((d) => d.results ?? []);
  const closing = Object.values(store.days).flatMap((d) => d.closing ?? []);
  if (!results.length && !closing.length) return;

  // INCLUDING THE DAYS THAT ARE NOT THERE. `loadClosingDays` leaves out a date
  // whose /api/closing fetch failed and had nothing cached, and a proposal is
  // filed under the day the pad was dealt rather than the day its race runs —
  // so a racing leg that settled from nobody's scoreboard could otherwise be
  // handed an empty day and stay pending for ever.
  for (const date of new Set([...closingByDate.keys(), ...due.keys(), ...proposalsByDate.keys()])) {
    const day = closingByDate.get(date) ?? { closing: [], results: [] };
    closingByDate.set(date, {
      ...day,
      results: [...(day.results ?? []), ...results],
      closing: [...(day.closing ?? []), ...closing],
    });
  }
}

// --- the paper trail -------------------------------------------------------------
//
// Everything the pad PROPOSED, settled from the same two days of closing rows
// the placed bets are settled from. Two days because a Saturday night game
// resolves on Sunday morning and today's pad is still being dealt.

/**
 * What a day's settled outcomes MEAN, without the timestamps that move on
 * every run. `resolveLeg` stamps `settledAt` with the current time, and a
 * proposal is re-settled from scratch each sync, so a plain deep-equal would
 * call every day changed every five minutes and rewrite localStorage for
 * nothing.
 */
const outcomeStamp = (settled) => JSON.stringify((settled ?? []).map((p) => [
  p.id, p.outcome, p.clv, p.staked, p.returned,
  (p.legs ?? []).map((l) => [l.matchKey, l.side, l.price, l.closingPrice, l.closingFair, l.clvSource, l.clv, l.result]),
]));

/**
 * Yesterday's and today's proposals, and the closing days they need.
 *
 * The dates go into the SAME `dateInfo` the placed bets built, so both are
 * fetched by the one `loadClosingDays` call — settling the paper trail must
 * never cost a second round trip. A day whose every proposal is already
 * decided is not marked unresolved, so its cached closing rows are kept
 * instead of asked for again.
 */
function collectProposals(dateInfo) {
  const stored = prefs[PROPOSAL_OUTCOMES_KEY] || {};
  const today = localDate(Date.now(), SYDNEY);
  const out = new Map();

  for (const date of [addDay(today, -1), today]) {
    let list = [];
    try { list = proposalsFor(date) ?? []; } catch { list = []; }
    if (!list.length) continue;
    out.set(date, list);

    const prior = stored[date];
    const allDecided = Array.isArray(prior) && prior.length === list.length
      && prior.every((p) => p?.outcome && p.outcome !== 'pending');
    const info = dateInfo.get(date) ?? { anyUnresolved: false };
    if (!allDecided) info.anyUnresolved = true;
    dateInfo.set(date, info);
  }
  return out;
}

/**
 * A freshly built proposal, with any hand marks the stored trail holds for it
 * carried back on.
 *
 * THE TRAIL IS RE-SETTLED FROM THE PROPOSALS, NOT FROM ITSELF: `collectProposals`
 * reads `proposalsFor(date)` — the sets as they were dealt — and settles them
 * again from scratch every sync. Without this, a leg somebody marked by hand
 * this morning would be rebuilt unmarked five minutes later, and the mark would
 * look as though it had never been made. `resolveLeg` already refuses to touch a
 * manual leg; this is what makes sure the leg still SAYS manual by the time it
 * gets there.
 *
 * Matched on the leg's own identity — match and side — rather than on its
 * position, because a set is rebuilt from its own record and a leg that moved
 * would otherwise take its neighbour's mark. Whatever the stored leg learned
 * from the close comes back with the mark, since a manual leg is frozen
 * afterwards and would otherwise lose a closing price it had already been given.
 */
function withHandMarks(proposal, prior) {
  const marks = new Map();
  for (const leg of prior?.legs ?? []) {
    if (leg?.resultSource === 'manual') marks.set(`${leg.matchKey}|${leg.side}`, leg);
  }
  if (!marks.size) return proposal;

  return {
    ...proposal,
    legs: (proposal?.legs ?? []).map((leg) => {
      const mark = marks.get(`${leg?.matchKey}|${leg?.side}`);
      if (!mark) return leg;
      return {
        ...leg,
        closingPrice: mark.closingPrice ?? leg.closingPrice ?? null,
        closingFair: mark.closingFair ?? leg.closingFair ?? null,
        clvSource: mark.clvSource ?? leg.clvSource ?? null,
        clvLegacy: mark.clvLegacy ?? leg.clvLegacy ?? null,
        clv: mark.clv ?? leg.clv ?? null,
        result: mark.result ?? null,
        resultSource: 'manual',
        settledAt: mark.settledAt ?? null,
      };
    }),
  };
}

/**
 * Settle those proposals and write the day back. Returns whether anything
 * actually changed — an unchanged day is not rewritten.
 */
async function settleProposals(proposalsByDate, closingByDate, nowIso) {
  const stored = { ...(prefs[PROPOSAL_OUTCOMES_KEY] || {}) };
  let changed = false;

  if (proposalsByDate.size) {
    // Imported here rather than at the top of the file: `lib/paper-trail.js`
    // is built on the pure half of THIS module, so a static import back would
    // close a cycle through it — and it drags the backtest store in for
    // MIN_BETS, which the app shell has no reason to load before History is
    // opened. Same shape as `authHeaders` above.
    const { settleProposal } = await import('../paper-trail.js');
    for (const [date, list] of proposalsByDate) {
      const day = closingByDate.get(date);
      const prior = new Map((stored[date] ?? []).map((p) => [p?.id, p]));
      const settled = list.map((p) => settleProposal(
        withHandMarks(p, prior.get(p?.id)), day?.closing ?? [], day?.results ?? [], nowIso,
      ));
      if (outcomeStamp(settled) === outcomeStamp(stored[date])) continue;
      stored[date] = settled;
      changed = true;
    }
  }

  const cutoff = addDay(localDate(Date.now(), SYDNEY), -OUTCOME_KEEP_DAYS);
  for (const date of Object.keys(stored)) {
    if (date >= cutoff) continue;
    delete stored[date];
    changed = true;
  }

  if (changed) prefs[PROPOSAL_OUTCOMES_KEY] = stored;

  await rollUp(stored);
  return changed;
}

/**
 * Settle the legs the gate refused, off the same closing rows the proposals
 * were just settled from.
 *
 * LAST, AND ITS OWN TRY. This is the least important record in the app and it
 * must not be able to cost a proposal its settlement, let alone a bet. It also
 * buys nothing: `closingByDate` is already in hand, and a day with no closing
 * rows simply leaves its refusals unscored until the next sync.
 *
 * Nothing here is a proposal, so nothing here reaches `rollUp`. Insights reads
 * this store on its own (`refusedAgg` in `lib/rejections.js`).
 */
async function settleRejections(closingByDate, nowIso) {
  const stored = { ...(prefs[REJECTION_OUTCOMES_KEY] || {}) };
  let changed = false;

  if (closingByDate.size) {
    const { settleProposal } = await import('../paper-trail.js');
    for (const [date, day] of closingByDate) {
      const list = rejectionsFor(date);
      if (!list.length) continue;
      const settled = list.map((row) => settleProposal(
        row, day?.closing ?? [], day?.results ?? [], nowIso,
      ));
      if (outcomeStamp(settled) === outcomeStamp(stored[date])) continue;
      stored[date] = settled;
      changed = true;
    }
  }

  const cutoff = addDay(localDate(Date.now(), SYDNEY), -OUTCOME_KEEP_DAYS);
  for (const date of Object.keys(stored)) {
    if (date >= cutoff) continue;
    delete stored[date];
    changed = true;
  }

  if (changed) prefs[REJECTION_OUTCOMES_KEY] = stored;
  return changed;
}

/**
 * Fold the trail down into the all-time record Insights reads.
 *
 * Runs whether or not the day changed, and deliberately: the fold has its own
 * clock in it — days pass 120 old and want moving into their month — so a quiet
 * morning with nothing to settle is still a morning where the rollup moves.
 * `rollupFrom` is idempotent, so running it on an unchanged trail is a no-op
 * that costs one JSON compare.
 *
 * Late-imported for the reason `settleProposal` above is: this is not on the
 * boot path, and the app shell has no business loading the fold before anybody
 * has opened Insights. It is also why nothing here throws outward — the whole
 * call already sits inside `syncSettlement`'s try, and the record of what the
 * app said must never be able to cost a placed bet its settlement.
 */
async function rollUp(stored) {
  const { rollupFrom, readRollup, ROLLUP_KEY } = await import('../insights-rollup.js');
  const prev = readRollup();
  const next = rollupFrom(prev, stored, localDate(Date.now(), SYDNEY));
  // Same trick as `outcomeStamp`: localStorage is written on a phone, and a
  // rollup that came out identical is not worth the write.
  if (JSON.stringify(next) !== JSON.stringify(prev)) prefs[ROLLUP_KEY] = next;
}

/**
 * Tell the app the trail moved, so `stores/paper-trail.js` can re-read it.
 *
 * Late import: `core/state.js` registers window listeners as it loads, and a
 * module-scope import would drag a browser into the node test runner — the
 * same reason `store.js` reaches for the bus this way.
 */
function announceSettled() {
  if (typeof window === 'undefined') return;
  import('../../core/state.js').then(({ bus }) => bus.emit('settled')).catch(() => {});
}

// --- marking the trail by hand ---------------------------------------------------
//
// A proposal was never backed, so no bookmaker will ever settle it — but the
// pad's own record still has to be right. A match whose score never arrived
// leaves a proposal reading "No result" for ever, and its strategy's record
// wrong for ever with it, and the only person who can fix that is the one who
// watched the game.
//
// ONE WRITE PATH, and it is the settle's own. A leg is stamped `manual` — which
// `resolveLeg` then refuses to touch, exactly as it does on a placed bet — and
// the proposal is re-derived through `settleProposal`, so its outcome, its
// return and its CLV come out of `betOutcome`/`returnedFor`/`betClv` the same
// way the morning settle's do. There is no second arithmetic here, which is why
// a hand mark and an auto settle can never disagree about what a ticket with
// these legs did. The rollup is re-folded and `settled` announced exactly as
// `settleProposals` does, so History and Insights re-read on the spot.

/** Where a proposal sits in the trail: `{ date, index, proposal, list }`, or null. */
function locateProposal(trail, proposalId) {
  for (const [date, list] of Object.entries(trail ?? {})) {
    if (!Array.isArray(list)) continue;
    const index = list.findIndex((p) => p?.id === proposalId);
    if (index >= 0) return { date, index, proposal: list[index], list };
  }
  return null;
}

/**
 * The trail as this device holds it.
 *
 * /demo reads the invented fortnight out of the paper-trail store instead of
 * storage, because that is the only trail it has. Late-imported for the reason
 * everything else in this file is: `stores/paper-trail.js` is built on
 * `lib/paper-trail.js`, which is built on the pure half of THIS module.
 */
async function readTrail() {
  if (!isDemo.value) return prefs[PROPOSAL_OUTCOMES_KEY] || {};
  const { outcomes } = await import('./paper-trail.js');
  return outcomes.value || {};
}

/**
 * One leg, marked by hand. `null` clears the mark AND the source, which hands
 * the leg straight back to auto-settle — `resolveLeg` only freezes a leg while
 * it says `manual`.
 */
const handMarkedLeg = (leg, result, nowIso) => ({
  ...leg,
  result: result ?? null,
  resultSource: result ? 'manual' : null,
  settledAt: result ? nowIso : null,
});

/**
 * Re-derive the proposal from those legs and write the day back.
 *
 * The empty closing rows and results handed to `settleProposal` are not a
 * short-cut: `resolveLeg` returns a manual leg untouched and leaves an
 * unmarked one exactly as it found it when there is nothing new to say, so the
 * legs pass through unchanged and only the ticket's own figures are recomputed.
 *
 * /DEMO WRITES NOTHING. The mark lands in the in-memory trail so the control
 * can be seen working, and is gone on the next reload — no `prefs`, no rollup,
 * and no `settled` (which would only make `reloadOutcomes` throw it away).
 */
async function writeMarkedProposal(found, legs, nowIso) {
  const { settleProposal } = await import('../paper-trail.js');
  const next = settleProposal({ ...found.proposal, legs }, [], [], nowIso);
  const list = found.list.map((p, i) => (i === found.index ? next : p));

  if (isDemo.value) {
    const { outcomes } = await import('./paper-trail.js');
    outcomes.value = { ...outcomes.value, [found.date]: list };
    return next;
  }

  const stored = { ...(prefs[PROPOSAL_OUTCOMES_KEY] || {}), [found.date]: list };
  prefs[PROPOSAL_OUTCOMES_KEY] = stored;
  await rollUp(stored);
  announceSettled();
  return next;
}

/**
 * Mark one leg of a proposal won, lost or void by hand — or `null` to clear the
 * mark and hand the leg back to the morning settle.
 *
 * Returns the re-settled proposal, or `null` when the trail no longer holds it
 * (a day pruned past sixty, a leg index that is not there).
 */
export async function markProposalLeg(proposalId, legIndex, result) {
  if (result != null && !['won', 'lost', 'void'].includes(result)) return null;
  const found = locateProposal(await readTrail(), proposalId);
  if (!found) return null;

  const legs = found.proposal?.legs ?? [];
  if (!legs[legIndex]) return null;

  const nowIso = new Date().toISOString();
  const marked = legs.map((leg, i) => (i === legIndex ? handMarkedLeg(leg, result, nowIso) : leg));
  return writeMarkedProposal(found, marked, nowIso);
}

/**
 * Mark a whole proposal won, lost, void or back to pending.
 *
 * THE OUTCOME IS STILL DERIVED FROM THE LEGS — there is no field on a proposal
 * that says "won" on its own, and inventing one would be the second arithmetic
 * this module does not have. So the ask is turned into the leg pattern that
 * `betOutcome` reads as the outcome asked for:
 *
 *   won      every leg won. `betOutcome` wants no lost leg and no undecided
 *            one, and won is the only mark that gives both.
 *   void     every leg void — the one pattern `betOutcome` calls void.
 *   lost     one lost leg loses the ticket whatever the others say, so every
 *            leg with no result is marked lost and every leg already decided is
 *            LEFT ALONE: a ticket that lost on one leg did not lose on all four,
 *            and a record that says it did is a record of something that did not
 *            happen. If that leaves nothing lost — every leg was already marked
 *            won or void — the first leg is marked lost, because a person who
 *            has just said the ticket lost must not be left with a record that
 *            derives "won".
 *   pending  every mark cleared, source and all: the ticket goes back to
 *            undecided and the next settle looks again.
 */
export async function markProposal(proposalId, outcome) {
  if (!['won', 'lost', 'void', 'pending'].includes(outcome)) return null;
  const found = locateProposal(await readTrail(), proposalId);
  if (!found) return null;

  const legs = found.proposal?.legs ?? [];
  if (!legs.length) return null;

  const nowIso = new Date().toISOString();
  let marked;
  if (outcome === 'pending') {
    marked = legs.map((leg) => handMarkedLeg(leg, null, nowIso));
  } else if (outcome === 'lost') {
    marked = legs.map((leg) => (leg?.result ? leg : handMarkedLeg(leg, 'lost', nowIso)));
    if (!marked.some((leg) => leg?.result === 'lost')) marked[0] = handMarkedLeg(marked[0], 'lost', nowIso);
  } else {
    marked = legs.map((leg) => handMarkedLeg(leg, outcome, nowIso));
  }

  return writeMarkedProposal(found, marked, nowIso);
}

/** Ask the server to run the closing job on the punter's own key, best-effort. */
function triggerForegroundClosing() {
  let key = '';
  try {
    key = getOddsApiKey();
  } catch {
    key = '';
  }
  if (!key) return;
  try {
    fetch(`${apiBase()}/api/jobs/closing`, { method: 'POST', headers: keyHeaders() }).catch(() => {});
  } catch {
    // No fetch in this environment. Nothing to spend it on either way.
  }
}

/** The shape the server will accept for a board: `SPORT_KEY_RE` in server/odds.mjs. */
const SPORT_KEY_RE = /^[a-z0-9_]{1,50}$/;

/**
 * The Odds API keys a set of legs is waiting on.
 *
 * A leg names its sport the way the engine does — `afl`, `epl` — and for a
 * sport the registry does not carry it is the provider's own key instead
 * (`baseball_mlb`), because that is what `identify` falls back to when it
 * builds the matchKey. `?sports=` speaks only the provider's keys, so the
 * registry is read backwards here, and a name it does not know already IS one.
 *
 * Anything malformed is dropped rather than sent: the whole request would come
 * back `bad-sports`, and one unreadable leg must not cost the others their
 * settlement.
 */
function sportsToSettle(legs) {
  const keys = new Set();
  for (const leg of legs) {
    const sport = leg?.sport || String(leg?.matchKey ?? '').split(':')[0];
    if (!sport) continue;
    for (const key of SPORTS[sport]?.oddsApiKeys ?? [sport]) {
      if (SPORT_KEY_RE.test(key)) keys.add(key);
    }
  }
  return [...keys].sort();
}

/**
 * Ask the server to settle, on the punter's own key, and WAIT for it.
 *
 * Awaited, unlike the closing trigger above, because this is the call that
 * writes the results the sync below is about to read: returning early would
 * settle from the same data as last time, and the tap would look like it did
 * nothing — which is exactly the complaint this path exists to answer.
 *
 * Narrow on purpose. Only the boards and dates this device's own unsettled bets
 * are waiting on: a scores call costs 2 credits a sport a date out of the
 * punter's own 500 a month, so a refresh must never pay for a board nobody here
 * bet on. Nothing outstanding means no request at all — zero credits is the
 * right answer for a clear History, which is most taps. The server caps both
 * lists again at its end; asking narrowly is what keeps the bill down, asking
 * legally is what keeps it possible.
 */
async function triggerForegroundSettle(sports, dates) {
  if (!sports.length) return;
  let key = '';
  try {
    key = getOddsApiKey();
  } catch {
    key = '';
  }
  if (!key) return;
  const query = new URLSearchParams({ sports: sports.join(',') });
  if (dates.length) query.set('dates', dates.join(','));
  try {
    await fetch(`${apiBase()}/api/jobs/settle?${query}`, { method: 'POST', headers: keyHeaders() });
  } catch {
    // Offline, or the job refused. Whatever is already in storage still settles
    // below, and the next sync asks again.
  }
}

/**
 * Bring every placed bet's legs up to date, and settle whatever that
 * resolves. Demo is a no-op; otherwise at most once every five minutes
 * unless `force`.
 *
 * `settle` first asks the SERVER to go and find the results, on this punter's
 * key. Off by default: every automatic sync — boot, and coming back to the
 * foreground — reads what is already there and spends nothing, and the one
 * caller that passes it is the refresh button (see `refreshToday` in store.js),
 * which is a deliberate tap on a deliberate control.
 *
 * Answers `{ settled }` — how many PLACED bets this pass took off pending. The
 * refresh toast says it out loud, so a tap that settled something says so
 * rather than leaving the punter to go and look. A pass that did nothing, or
 * was skipped by the floor or by one already running, answers zero.
 */
export async function syncSettlement({ force = false, settle = false } = {}) {
  const none = { settled: 0 };
  if (isDemo.value) return none;
  const startedAt = Date.now();
  if (!force && lastSync.value && startedAt - lastSync.value < SYNC_MIN_MS) return none;
  if (settling.value) return none;

  let settledCount = 0;
  settling.value = true;
  try {
    await storeReady;
    triggerForegroundClosing();

    const rows = await records.all().catch(() => []);
    const legsByMulti = new Map();
    for (const row of rows) {
      if (row?.entity !== 'leg') continue;
      const list = legsByMulti.get(row.multiId) ?? [];
      list.push(row);
      legsByMulti.set(row.multiId, list);
    }
    // "Proposed or placed" — but only a PLACED multi is ever actually stored,
    // so this is what "stored" means in practice; a same-day proposed ticket
    // that kicked off and was never placed has no legs in `rows` to find.
    const multiRows = rows.filter((r) => r?.entity === 'multi' && (r.status === 'placed' || r.status === 'proposed'));

    const now = Date.now();
    const dueLeg = (leg) => {
      const t = Date.parse(leg?.commenceTime);
      return Number.isFinite(t) && t < now;
    };

    // Which bets are actually still open. A leg belonging to a settled bet is
    // history — it may well be unresolved on a void or a leg nobody marked —
    // and paying to find out how it finished buys nothing that changes.
    const pending = new Set(betRecords.value.filter((b) => b.result === 'pending').map((b) => b.multiId));

    const dateInfo = new Map();
    // The legs a settle would be FOR: kicked off, no result, on a pending bet.
    // What the server is asked about is derived from these and nothing else.
    const waiting = [];
    for (const multi of multiRows) {
      for (const leg of legsByMulti.get(multi.id) ?? []) {
        if (!dueLeg(leg)) continue;
        const date = localDate(leg.commenceTime, SYDNEY);
        const info = dateInfo.get(date) ?? { anyUnresolved: false };
        if (leg.result == null) info.anyUnresolved = true;
        dateInfo.set(date, info);
        if (leg.result == null && pending.has(multi.id)) waiting.push({ leg, date });
      }
    }
    // The pad's own two days, folded into the same fetch. A device that has
    // never placed a bet still has a paper trail, so this is also what stops
    // the early return below from being the end of the story.
    const proposalsByDate = collectProposals(dateInfo);

    if (!dateInfo.size) {
      lastSync.value = Date.now();
      return none;
    }

    // SETTLE FIRST, THEN READ. The server writes its results into the very days
    // `loadClosingDays` is about to fetch, so the order is the whole trick: ask
    // it to go and look, wait, and only then read the day back.
    if (settle) {
      await triggerForegroundSettle(
        sportsToSettle(waiting.map((w) => w.leg)),
        [...new Set(waiting.map((w) => w.date))].sort(),
      );
    }

    const closingByDate = await loadClosingDays(dateInfo, { force });

    // Racing's own results and closing lines, on the punter's PuntersEdge key
    // and inside a fixed daily allowance, merged into the days above. Only ever
    // reached when a racing leg is pending on a race that has already run, and
    // never at a football bet's expense — see the section's header.
    try {
      await settleRacing(closingByDate, waiting, proposalsByDate, now);
    } catch {
      // Offline, no key, or the upstream refused. Everything already bought is
      // still in the merge above, and the next sync asks again.
    }

    const nowIso = new Date().toISOString();

    const changedLegs = [];
    for (const multi of multiRows) {
      const legsFor = legsByMulti.get(multi.id) ?? [];
      const nextLegs = legsFor.map((leg) => {
        if (!dueLeg(leg)) return leg;
        const day = closingByDate.get(localDate(leg.commenceTime, SYDNEY));
        if (!day) return leg;
        const next = resolveLeg(leg, day.results, day.closing, nowIso);
        if (next !== leg) changedLegs.push(next);
        return next;
      });
      legsByMulti.set(multi.id, nextLegs);
    }
    if (changedLegs.length) {
      await Promise.all(changedLegs.map((l) => records.put({ ...l }).catch(() => undefined)));
    }

    for (const bet of betRecords.value) {
      if (bet.result !== 'pending') continue;
      const legsFor = legsByMulti.get(bet.multiId) ?? [];
      if (!legsFor.length || !shouldAutoSettle(bet, legsFor)) continue;

      const outcome = betOutcome(legsFor);
      const returned = returnedFor(bet.stake, legsFor);
      const settled = await recordResult(bet.id, outcome, returned);
      if (!settled) continue;

      const patched = {
        ...settled,
        resultSource: 'auto',
        clv: betClv(legsFor),
        pnl: cents((Number(settled.returned) || 0) - (Number(settled.stake) || 0)),
      };
      await records.put({ ...patched }).catch(() => undefined);
      betRecords.value = betRecords.value.map((b) => (b.id === bet.id ? patched : b));
      settledCount += 1;
    }

    // Last, and never at a placed bet's expense: real money is settled above,
    // and the trail is a record of what the app said. A broken record must not
    // be able to cost a bet its settlement.
    try {
      await settleProposals(proposalsByDate, closingByDate, nowIso);
    } catch {
      // Nothing written. The next sync settles the same day again.
    }

    // And the refusal log, after everything that matters. See `settleRejections`.
    try {
      await settleRejections(closingByDate, nowIso);
    } catch {
      // The same next sync picks it up.
    }

    lastSync.value = Date.now();
    return { settled: settledCount };
  } finally {
    settling.value = false;
    announceSettled();
  }
}

let started = false;

/**
 * Wire settlement into the app's lifecycle: once, ever. Runs once storage is
 * ready, and again every time the app comes back to the foreground.
 */
export function startSettlement() {
  if (started) return;
  started = true;
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncSettlement();
    });
  }
  storeReady.then(() => syncSettlement());
}
